import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Upload, FileText, Clock, CheckCircle, AlertCircle, XCircle, ArrowRight, Sparkles, Trash2, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Actions as IMPORT_ACTIONS } from '../store/actions/intelligentImportActions';
import { SUPPORTED_FILE_TYPES, SUPPORTED_MIME_TYPES, SOURCE_KINDS, PIPELINE_STATUSES } from '../constants/importConstants';
import { newSelect as Select } from '../components/ui/newSelect';

const statusConfig = {
  [PIPELINE_STATUSES.COMPLETE]: { icon: CheckCircle, className: 'bg-success-bg text-success', label: 'Complete' },
  [PIPELINE_STATUSES.PARTIAL_COMPLETE]: { icon: AlertCircle, className: 'bg-warning-bg text-warning', label: 'Partial' },
  [PIPELINE_STATUSES.PENDING_REVIEW]: { icon: Clock, className: 'bg-info-bg text-info', label: 'Review' },
  [PIPELINE_STATUSES.FAILED]: { icon: XCircle, className: 'bg-danger-bg text-danger', label: 'Failed' },
  [PIPELINE_STATUSES.CANCELLED]: { icon: XCircle, className: 'bg-muted text-muted-foreground', label: 'Cancelled' },
};

const sourceHintOptions = [
  { value: 'UNSPECIFIED', label: 'Auto-detect' },
  { value: 'BANK', label: 'Bank Statement' },
  { value: 'LEDGER', label: 'Ledger Export' },
  { value: 'SUPPLIER', label: 'Supplier Invoice' },
  { value: 'DOCUMENT', label: 'Receipt / Document' },
];

const SmartImportGuideContent = () => (
  <div className='space-y-5 text-sm text-foreground max-h-[60vh] overflow-y-auto pr-1'>
    <section className='space-y-2 mt-2'>
      <h3 className='font-semibold text-foreground'>What is Smart Import?</h3>
      <p className='text-muted-foreground'>
        Smart Import turns raw financial files into carbon usage data. Upload a bank statement, accounting export,
        supplier invoice, or receipt — the system parses rows, classifies the source, matches each line to emission
        factors with AI, and lets you review before anything is written to your usage data.
      </p>
    </section>

    <section className='space-y-2'>
      <h3 className='font-semibold text-foreground'>1. Upload a file</h3>
      <ul className='list-disc pl-5 space-y-1 text-muted-foreground'>
        <li>Drag and drop a file onto the upload area, or click to browse.</li>
        <li>
          Supported formats: CSV, XLSX, PDF, and images (JPEG, PNG, TIFF, WebP) — useful for scanned receipts and
          invoices.
        </li>
        <li>
          <span className='text-foreground font-medium'>Source type hint</span> helps the parser when you already know
          what you uploaded. Choose Auto-detect if unsure; otherwise pick Bank Statement, Ledger Export, Supplier
          Invoice, or Receipt / Document.
        </li>
        <li>Click <span className='text-foreground font-medium'>Start Import</span> to upload and begin processing. You are taken to the import job page automatically.</li>
      </ul>
    </section>

    <section className='space-y-2'>
      <h3 className='font-semibold text-foreground'>2. Processing pipeline</h3>
      <p className='text-muted-foreground'>
        After upload, the job runs through automated stages: parse → classify → stage → deduplicate → match. A
        progress bar at the top of the job page shows where you are. While processing, wait on that page — status
        updates automatically.
      </p>
      <p className='text-muted-foreground'>You may be asked to help at specific steps:</p>
      <ul className='list-disc pl-5 space-y-1 text-muted-foreground'>
        <li>
          <span className='text-foreground font-medium'>File type unclear</span> — choose whether the file is a bank
          statement, ledger export, or supplier invoice (sample rows are shown to help).
        </li>
        <li>
          <span className='text-foreground font-medium'>Date format uncertain</span> — confirm how dates are written
          in your file (e.g. DD/MM/YYYY vs MM/DD/YYYY) so transactions are interpreted correctly.
        </li>
        <li>
          <span className='text-foreground font-medium'>Receipt or document</span> — review OCR-extracted fields and
          link the document to related spend lines where needed.
        </li>
      </ul>
    </section>

    <section className='space-y-2'>
      <h3 className='font-semibold text-foreground'>3. Review factor matches</h3>
      <p className='text-muted-foreground'>
        For bank, ledger, and supplier files, each staged row is matched to a carbon conversion factor. Review the
        suggested matches: confirm correct ones, reject wrong ones, or pick an alternative factor. High-confidence
        matches may be pre-selected; always check material or unusual lines. You can review in bulk where the UI
        offers it.
      </p>
      <p className='text-muted-foreground'>
        Duplicates and bank-statement rows filtered by rules are skipped automatically and shown in staging stats.
      </p>
    </section>

    <section className='space-y-2'>
      <h3 className='font-semibold text-foreground'>4. Commit to usage data</h3>
      <p className='text-muted-foreground'>
        When review is complete, commit confirmed rows into your organisation&apos;s usage data. After a successful
        commit you can open Usage Data or Reports, or start another import. Status <span className='text-foreground font-medium'>Partial</span> means some rows still need review or failed to commit — open the job from history to finish.
      </p>
    </section>

    <section className='space-y-2'>
      <h3 className='font-semibold text-foreground'>5. Import history</h3>
      <p className='text-muted-foreground'>
        The table lists past imports for your current organisation. Click any row to open that job and continue
        review, commit, or inspect results. Columns show file name, detected source, status, row counts, transaction
        period, and upload date.
      </p>
      <ul className='list-disc pl-5 space-y-1 text-muted-foreground'>
        <li><span className='text-foreground font-medium'>Complete</span> — finished successfully.</li>
        <li><span className='text-foreground font-medium'>Review</span> — waiting for your input (classification, dates, or factor review).</li>
        <li><span className='text-foreground font-medium'>Partial</span> — partly committed; more rows may need action.</li>
        <li><span className='text-foreground font-medium'>Failed</span> — open the job to see the error; you can retry matching or commit where offered.</li>
      </ul>
    </section>

    <section className='space-y-2'>
      <h3 className='font-semibold text-foreground'>Tips</h3>
      <ul className='list-disc pl-5 space-y-1 text-muted-foreground'>
        <li>Ensure the correct organisation is selected before uploading — history and commits are per organisation.</li>
        <li>Cleaner exports (consistent columns, clear dates, GBP amounts where expected) improve matching accuracy.</li>
        <li>For template-based category imports (e.g. electricity, travel), use <span className='text-foreground font-medium'>Import usage data</span> on the Usage page instead.</li>
        <li>After import, check data quality from the Quality area and your usage data table for tier badges on new records.</li>
      </ul>
    </section>
  </div>
);

/** ISO YYYY-MM-DD → locale date string without UTC day-shift. */
const formatIsoDateLocal = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

const formatStagingTransactionPeriod = (job) => {
  const min = job.stagingTransactionDateMin ?? job.staging_transaction_date_min;
  const max = job.stagingTransactionDateMax ?? job.staging_transaction_date_max;
  const dMin = formatIsoDateLocal(min);
  const dMax = formatIsoDateLocal(max);
  if (!dMin && !dMax) return null;
  if (dMin && dMax && dMin !== dMax) return `${dMin} – ${dMax}`;
  return dMin || dMax;
};

const IntelligentImport = ({
  jobs,
  loading,
  initiateImport,
  startImport,
  getImportHistory,
  deleteImport,
  purgeAllImports,
  currentOrganisation,
  cyfAdmin,
  whiteLabelAdmin,
}) => {
  const history = useHistory();
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sourceHint, setSourceHint] = useState('UNSPECIFIED');
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const isImportAdmin = Boolean(cyfAdmin || whiteLabelAdmin);

  useEffect(() => {
    if (currentOrganisation) getImportHistory();
  }, [currentOrganisation]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    if (e.target.files?.[0]) {
      setSelectedFile(e.target.files[0]);
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const importId = await initiateImport(selectedFile, sourceHint);
      if (importId) {
        await startImport(importId);
        history.push(`/intelligent-import/${importId}`);
      }
    } finally {
      setUploading(false);
    }
  }, [selectedFile, sourceHint, initiateImport, startImport, history]);

  const getStatusBadge = useCallback((status) => {
    const cfg = statusConfig[status] || { className: 'bg-muted text-muted-foreground', label: status };
    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget?.id) return;
    const ok = await deleteImport(deleteTarget.id);
    if (ok) setDeleteTarget(null);
  }, [deleteTarget, deleteImport]);

  const handleConfirmPurge = useCallback(async () => {
    const ok = await purgeAllImports();
    if (ok) setPurgeOpen(false);
  }, [purgeAllImports]);

  return (
    <div className='flex flex-col gap-6 p-6'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-bold text-foreground flex items-center gap-2'>
            <Sparkles size={24} strokeWidth={1.5} />
            Smart Import
          </h1>
          <p className='text-muted-foreground mt-1'>
            Upload financial files and let AI classify, match, and import your carbon data
          </p>
        </div>
        <Button type='button' variant='link' className='text-primary shrink-0 px-0' onClick={() => setGuideOpen(true)}>
          <BookOpen size={16} strokeWidth={1.5} />
          See the guide
        </Button>
      </div>

      <Card className='bg-card'>
        <CardHeader>
          <CardTitle className='text-lg'>Upload File</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex flex-col gap-4'>
            <div
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type='file'
                className='hidden'
                accept={SUPPORTED_FILE_TYPES.join(',')}
                onChange={handleFileSelect}
              />
              <Upload size={40} className='mx-auto mb-3 text-muted-foreground' strokeWidth={1.5} />
              {selectedFile ? (
                <div>
                  <p className='font-medium text-foreground'>{selectedFile.name}</p>
                  <p className='text-sm text-muted-foreground mt-1'>
                    {(selectedFile.size / 1024).toFixed(1)} KB &middot; {selectedFile.type || 'unknown type'}
                  </p>
                </div>
              ) : (
                <div>
                  <p className='font-medium text-foreground'>Drop your file here or click to browse</p>
                  <p className='text-sm text-muted-foreground mt-1'>
                    Supports CSV, XLSX, PDF, and image files (receipts, invoices)
                  </p>
                </div>
              )}
            </div>

            <div className='flex items-end gap-4'>
              <div className='flex-1'>
                <label className='text-sm font-medium text-foreground mb-1.5 block'>Source Type Hint</label>
                <Select
                  value={sourceHint}
                  onValueChange={setSourceHint}
                  options={sourceHintOptions}
                  placeholder='Select source type'
                />
              </div>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                loading={uploading}
                className='min-w-[160px]'
              >
                <Upload size={16} strokeWidth={1.5} />
                Start Import
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className='bg-card'>
        <CardHeader className='flex flex-row flex-wrap items-center justify-between gap-2'>
          <CardTitle className='text-lg'>Import History</CardTitle>
          {isImportAdmin && jobs.length > 0 && (
            <Button
              type='button'
              variant='destructive'
              size='sm'
              loading={Boolean(loading?.purgeImports)}
              disabled={Boolean(loading?.purgeImports)}
              onClick={() => setPurgeOpen(true)}
            >
              <Trash2 size={16} strokeWidth={1.5} />
              Purge all imports (admin)
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading?.history ? (
            <div className='text-center py-8 text-muted-foreground'>Loading import history...</div>
          ) : jobs.length === 0 ? (
            <div className='text-center py-8 text-muted-foreground'>
              <FileText size={32} className='mx-auto mb-2' strokeWidth={1.5} />
              No imports yet. Upload a file to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Import</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className='text-right'>Rows</TableHead>
                  <TableHead className='text-right'>Matched</TableHead>
                  <TableHead>Period covered</TableHead>
                  <TableHead>Upload date</TableHead>
                  {isImportAdmin ? <TableHead className='w-[100px] text-right'>Admin</TableHead> : null}
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow
                    key={job.id}
                    className='cursor-pointer hover:bg-muted/50'
                    onClick={() => history.push(`/intelligent-import/${job.id}`)}
                  >
                    <TableCell className='font-medium'>{job.label || job.filename || '—'}</TableCell>
                    <TableCell>{job.source === 'connector' ? (job.connectorId || 'Connector') : (job.detectedSourceKind || job.sourceHint || '—')}</TableCell>
                    <TableCell>{getStatusBadge(job.pipelineStatus)}</TableCell>
                    <TableCell className='text-right'>{job.totalRows ?? '—'}</TableCell>
                    <TableCell className='text-right'>{job.matchedRows ?? '—'}</TableCell>
                    <TableCell className='text-sm text-muted-foreground'>
                      {formatStagingTransactionPeriod(job) ?? '—'}
                    </TableCell>
                    <TableCell className='text-sm text-muted-foreground'>
                      {job.createdAt ? new Date(job.createdAt).toLocaleDateString() : '—'}
                    </TableCell>
                    {isImportAdmin ? (
                      <TableCell className='text-right' onClick={(e) => e.stopPropagation()}>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type='button'
                                variant='ghost'
                                size='icon'
                                className='text-destructive hover:bg-destructive/10'
                                aria-label='Delete import permanently'
                                onClick={() => setDeleteTarget({ id: job.id, filename: job.label || job.filename })}
                              >
                                <Trash2 size={16} strokeWidth={1.5} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className='bg-popover text-popover-foreground border-border'>
                              Delete import (admin)
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <ArrowRight size={16} className='text-muted-foreground' strokeWidth={1.5} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className='max-w-2xl bg-card text-card-foreground border-border'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <BookOpen size={20} strokeWidth={1.5} />
              Smart Import guide
            </DialogTitle>
          </DialogHeader>
          <DialogBody className='pt-0'>
            <SmartImportGuideContent />
          </DialogBody>
          <DialogFooter>
            <Button type='button' onClick={() => setGuideOpen(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className='bg-card text-card-foreground border-border'>
          <DialogHeader>
            <DialogTitle>Delete this import?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className='text-sm text-foreground'>
              This permanently removes the import <span className='font-medium'>{deleteTarget?.filename}</span> and all
              staging data. This cannot be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type='button'
              variant='destructive'
              loading={Boolean(loading?.deleteImport)}
              disabled={Boolean(loading?.deleteImport)}
              onClick={handleConfirmDelete}
            >
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <DialogContent className='bg-card text-card-foreground border-border'>
          <DialogHeader>
            <DialogTitle>Purge all imports for this organisation?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className='text-sm text-foreground'>
              This deletes every Smart Import job and associated data for the current organisation. Use only for admin
              cleanup or testing. This cannot be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => setPurgeOpen(false)}>
              Cancel
            </Button>
            <Button
              type='button'
              variant='destructive'
              loading={Boolean(loading?.purgeImports)}
              disabled={Boolean(loading?.purgeImports)}
              onClick={handleConfirmPurge}
            >
              Purge all imports
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  jobs: state.intelligentImport?.jobs || [],
  loading: state.intelligentImport?.loading || {},
  currentOrganisation: state.currentOrganisation,
  cyfAdmin: state.profile?.details?.cyfAdmin === true,
  whiteLabelAdmin: state.profile?.details?.whiteLabelAdmin === true,
});

const mapDispatchToProps = {
  initiateImport: IMPORT_ACTIONS.initiateImport,
  startImport: IMPORT_ACTIONS.startImport,
  getImportHistory: IMPORT_ACTIONS.getImportHistory,
  deleteImport: IMPORT_ACTIONS.deleteImport,
  purgeAllImports: IMPORT_ACTIONS.purgeAllImports,
};

export default connect(mapStateToProps, mapDispatchToProps)(IntelligentImport);
